import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Layers,
  MessageSquare,
  Plus,
  Send,
  Download,
  UploadCloud,
  CheckCircle,
  Eye,
  Info,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { FileRecord, FileVersion, FileAnnotation } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface FileProofingModalProps {
  file: FileRecord;
  isOpen: boolean;
  onClose: () => void;
}

export const FileProofingModal: React.FC<FileProofingModalProps> = ({
  file,
  isOpen,
  onClose,
}) => {
  useEscapeKey(isOpen, onClose);
  const { token, user } = useAuth();
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [annotations, setAnnotations] = useState<FileAnnotation[]>([]);
  const [activeVersionNumber, setActiveVersionNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);

  // New annotation creation state
  const [pendingPin, setPendingPin] = useState<{ x: number; y: number } | null>(null);
  const [pinComment, setPinComment] = useState('');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<'preview' | 'comments'>('preview');

  // Uploading new version
  const [isUploadingVersion, setIsUploadingVersion] = useState(false);
  const newVersionInputRef = useRef<HTMLInputElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const fetchProofingData = async () => {
    if (!token || !file) return;
    try {
      setIsLoading(true);
      const res = await fetch(`/api/files/${file.id}/proofing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
        setAnnotations(data.annotations || []);
        if (data.versions && data.versions.length > 0) {
          // Default to latest version
          const maxVer = Math.max(...data.versions.map((v: FileVersion) => v.versionNumber));
          setActiveVersionNumber(maxVer);
        }
      }
    } catch (err) {
      console.error('Failed to load file proofing:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProofingData();
      setPendingPin(null);
      setPinComment('');
      setSelectedAnnotationId(null);
      setMobileTab('preview');
    }
  }, [isOpen, file.id]);

  if (!isOpen) return null;

  // Handle clicking on image preview to drop visual feedback pin
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);

    setPendingPin({ x, y });
    setSelectedAnnotationId(null);
  };

  const handleAddAnnotation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingPin || !pinComment.trim() || !token) return;

    try {
      const res = await fetch(`/api/files/${file.id}/annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          versionNumber: activeVersionNumber,
          posX: pendingPin.x,
          posY: pendingPin.y,
          comment: pinComment.trim(),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const newAnn: FileAnnotation = {
          id: data.id,
          fileId: file.id,
          versionNumber: activeVersionNumber,
          authorId: user?.id || 'admin',
          authorName: user?.fullName || 'User',
          posX: pendingPin.x,
          posY: pendingPin.y,
          comment: pinComment.trim(),
          isResolved: false,
          createdAt: new Date().toISOString(),
        };
        setAnnotations((prev) => [...prev, newAnn]);
        setPendingPin(null);
        setPinComment('');
        setSelectedAnnotationId(data.id);
      }
    } catch (err) {
      console.error('Failed to create annotation:', err);
    }
  };

  const handleUploadNewVersion = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected || !token) return;

    setIsUploadingVersion(true);
    const formData = new FormData();
    formData.append('file', selected);
    formData.append('changeLog', `Version updated by ${user?.fullName || 'team member'}`);

    try {
      const res = await fetch(`/api/files/${file.id}/versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        await fetchProofingData();
      }
    } catch (err) {
      console.error('Failed to stack new version:', err);
    } finally {
      setIsUploadingVersion(false);
      if (newVersionInputRef.current) newVersionInputRef.current.value = '';
    }
  };

  const activeVersion = versions.find((v) => v.versionNumber === activeVersionNumber);
  const activeAnnotations = annotations.filter(
    (a) => a.versionNumber === activeVersionNumber
  );

  const isImageFile = file.mimeType.startsWith('image/') || /\.(png|jpg|jpeg|webp|gif|svg)$/i.test(file.originalName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-surface-900 rounded-none sm:rounded-3xl w-full max-w-6xl h-full sm:h-[88vh] flex flex-col shadow-2xl border-0 sm:border border-surface-200 dark:border-surface-800 overflow-hidden">
        {/* Top Header */}
        <div className="p-3 sm:p-4 px-4 sm:px-6 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between bg-surface-50/50 dark:bg-surface-850 gap-2">
          <div className="flex items-center space-x-2.5 sm:space-x-3 truncate">
            <span className="p-1.5 sm:p-2 rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-400 shrink-0">
              <Layers className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
            <div className="truncate">
              <h3 className="font-bold text-xs sm:text-sm text-surface-900 dark:text-surface-100 truncate">
                {file.originalName}
              </h3>
              <p className="text-[10px] sm:text-[11px] text-surface-400 truncate hidden xs:block">
                Interactive Proofing & Asset Version Stacking
              </p>
            </div>
          </div>

          {/* Right Header Actions */}
          <div className="flex items-center space-x-1.5 sm:space-x-3 shrink-0">
            <a
              href={`/api/files/${file.id}/download`}
              className="flex items-center space-x-1 sm:space-x-1.5 px-2.5 sm:px-3 py-1.5 min-h-[36px] rounded-xl text-xs font-semibold bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 hover:bg-surface-100"
              title="Download Raw Asset"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Download</span>
            </a>

            <button
              onClick={() => newVersionInputRef.current?.click()}
              disabled={isUploadingVersion}
              className="flex items-center space-x-1 sm:space-x-1.5 px-2.5 sm:px-3 py-1.5 min-h-[36px] rounded-xl text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm"
              title="Upload New Version"
            >
              <UploadCloud className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{isUploadingVersion ? 'Uploading...' : 'New Version (v+1)'}</span>
              <span className="sm:hidden">v+1</span>
            </button>
            <input
              ref={newVersionInputRef}
              type="file"
              onChange={handleUploadNewVersion}
              className="hidden"
            />

            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-xl text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile Switcher Tab Bar */}
        <div className="flex md:hidden items-center p-1 bg-surface-200/60 dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700">
          <button
            onClick={() => setMobileTab('preview')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
              mobileTab === 'preview'
                ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-surface-600 dark:text-surface-400'
            }`}
          >
            Asset Preview
          </button>
          <button
            onClick={() => setMobileTab('comments')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
              mobileTab === 'comments'
                ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-surface-600 dark:text-surface-400'
            }`}
          >
            Proofing Pins ({activeAnnotations.length})
          </button>
        </div>

        {/* Modal Main Body */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Main Canvas / Proofing View Area */}
          <div className={`flex-1 bg-surface-100 dark:bg-surface-950 p-3 sm:p-6 flex flex-col items-center justify-center relative overflow-auto touch-scroll select-none ${mobileTab === 'preview' ? 'flex' : 'hidden md:flex'}`}>
            {/* Version Switcher Tabs */}
            <div className="absolute top-3 left-3 sm:top-4 sm:left-6 z-20 flex items-center space-x-1.5 bg-white/90 dark:bg-surface-900/90 backdrop-blur-md p-1.5 rounded-2xl border border-surface-200/80 dark:border-surface-800/80 shadow-md">
              <span className="text-[10px] sm:text-[11px] font-bold text-surface-400 uppercase tracking-wider px-1.5 sm:px-2">
                Versions:
              </span>
              {versions.map((v) => (
                <button
                  key={v.id}
                  onClick={() => {
                    setActiveVersionNumber(v.versionNumber);
                    setPendingPin(null);
                  }}
                  className={`px-2.5 sm:px-3 py-1 rounded-xl text-xs font-bold transition-all ${
                    activeVersionNumber === v.versionNumber
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
                  }`}
                >
                  v{v.versionNumber}
                </button>
              ))}
            </div>

            {/* Instruction tooltip */}
            <div className="hidden sm:flex absolute bottom-4 left-6 z-20 bg-surface-900/80 dark:bg-surface-100/80 text-white dark:text-surface-900 px-3 py-1.5 rounded-xl text-[11px] backdrop-blur-md items-center space-x-1.5 shadow-lg">
              <Info className="w-3.5 h-3.5" />
              <span>Click anywhere on the preview to drop a visual proofing comment pin.</span>
            </div>

            {/* Interactive Image Frame */}
            {isImageFile ? (
              <div
                ref={imageContainerRef}
                onClick={handleImageClick}
                className="relative max-w-full max-h-[90%] rounded-2xl overflow-hidden shadow-2xl border border-surface-200/50 dark:border-surface-800/50 cursor-crosshair group bg-surface-200/20"
              >
                <img
                  src={`/api/files/${file.id}/preview`}
                  alt={file.originalName}
                  className="max-h-[60vh] sm:max-h-[68vh] object-contain block select-none pointer-events-none"
                />

                {/* Render Existing Pins */}
                {activeAnnotations.map((ann, idx) => (
                  <div
                    key={ann.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAnnotationId(ann.id);
                      if (window.innerWidth < 768) {
                        setMobileTab('comments');
                      }
                    }}
                    style={{ left: `${ann.posX}%`, top: `${ann.posY}%` }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] shadow-lg cursor-pointer transition-transform hover:scale-125 z-10 ${
                      selectedAnnotationId === ann.id
                        ? 'bg-rose-600 text-white ring-4 ring-rose-400/40 scale-125'
                        : 'bg-brand-600 text-white ring-2 ring-white'
                    }`}
                  >
                    {idx + 1}
                  </div>
                ))}

                {/* Pending Pin Marker */}
                {pendingPin && (
                  <div
                    style={{ left: `${pendingPin.x}%`, top: `${pendingPin.y}%` }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-[10px] ring-4 ring-amber-300/50 animate-pulse z-20"
                  >
                    ?
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-surface-900 p-8 sm:p-12 rounded-2xl sm:rounded-3xl border border-surface-200 dark:border-surface-800 text-center max-w-md shadow-card mx-4">
                <Layers className="w-12 h-12 text-brand-500 mx-auto mb-4" />
                <h4 className="font-bold text-sm text-surface-900 dark:text-surface-100 mb-1">
                  Non-image Document / Media Asset
                </h4>
                <p className="text-xs text-surface-500 mb-6 leading-relaxed">
                  High-capacity storage asset registered. Visual pin proofing is active on image & design deliverables, and version timelines track all file formats.
                </p>
                <a
                  href={`/api/files/${file.id}/download`}
                  className="inline-flex items-center space-x-2 px-4 py-2 min-h-[38px] rounded-xl text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Asset</span>
                </a>
              </div>
            )}

            {/* Mobile floating pin input sheet */}
            {pendingPin && (
              <div className="md:hidden absolute bottom-2 left-2 right-2 z-30 p-3.5 bg-white dark:bg-surface-900 rounded-2xl shadow-elevated border border-amber-300 dark:border-amber-700">
                <form onSubmit={handleAddAnnotation}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold text-amber-800 dark:text-amber-300">
                      New Pin ({pendingPin.x}%, {pendingPin.y}%)
                    </span>
                    <button
                      type="button"
                      onClick={() => setPendingPin(null)}
                      className="text-surface-400 hover:text-surface-600 text-xs p-1"
                    >
                      Cancel
                    </button>
                  </div>
                  <textarea
                    autoFocus
                    rows={2}
                    value={pinComment}
                    onChange={(e) => setPinComment(e.target.value)}
                    placeholder="Describe feedback or markup request..."
                    className="w-full text-xs p-2 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-amber-500 mb-2"
                  />
                  <button
                    type="submit"
                    disabled={!pinComment.trim()}
                    className="w-full py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors"
                  >
                    Save Proofing Pin
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Right Panel: Feedback Threads & Version History */}
          <div className={`w-full md:w-80 lg:w-96 border-t md:border-t-0 md:border-l border-surface-200 dark:border-surface-800 bg-white dark:bg-surface-900 flex flex-col ${mobileTab === 'comments' ? 'flex flex-1 md:flex-none' : 'hidden md:flex'}`}>
            <div className="p-4 border-b border-surface-200 dark:border-surface-800">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-xs uppercase tracking-wider text-surface-500 flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1.5 text-brand-500" />
                  Proofing Pins (v{activeVersionNumber})
                </h4>
                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                  {activeAnnotations.length} comments
                </span>
              </div>
            </div>

            {/* List of Annotations */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {pendingPin && (
                <form
                  onSubmit={handleAddAnnotation}
                  className="p-3.5 rounded-2xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-800 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-amber-800 dark:text-amber-300">
                      New Pin at ({pendingPin.x}%, {pendingPin.y}%)
                    </span>
                    <button
                      type="button"
                      onClick={() => setPendingPin(null)}
                      className="text-surface-400 hover:text-surface-600 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                  <textarea
                    autoFocus
                    rows={2}
                    value={pinComment}
                    onChange={(e) => setPinComment(e.target.value)}
                    placeholder="Describe feedback, markup request, or copy edit..."
                    className="w-full text-xs p-2.5 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-amber-500 mb-2"
                  />
                  <button
                    type="submit"
                    disabled={!pinComment.trim()}
                    className="w-full py-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors"
                  >
                    Save Proofing Pin
                  </button>
                </form>
              )}

              {activeAnnotations.map((ann, idx) => {
                const isSelected = selectedAnnotationId === ann.id;
                return (
                  <div
                    key={ann.id}
                    onClick={() => setSelectedAnnotationId(ann.id)}
                    className={`p-3 rounded-2xl border transition-all cursor-pointer ${
                      isSelected
                        ? 'border-brand-500 bg-brand-50/30 dark:bg-brand-950/20 shadow-md ring-1 ring-brand-500'
                        : 'border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/40 hover:border-surface-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center space-x-2">
                        <span className="w-5 h-5 rounded-full bg-brand-600 text-white font-bold text-[10px] flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="font-semibold text-xs text-surface-900 dark:text-surface-100">
                          {ann.authorName}
                        </span>
                      </div>
                      <span className="text-[10px] text-surface-400">
                        {new Date(ann.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-surface-700 dark:text-surface-300 pl-7 leading-relaxed">
                      {ann.comment}
                    </p>
                  </div>
                );
              })}

              {activeAnnotations.length === 0 && !pendingPin && (
                <div className="text-center py-12 text-xs text-surface-400">
                  No proofing pins on version {activeVersionNumber} yet. Click anywhere on the asset preview to drop a pin.
                </div>
              )}
            </div>

            {/* Version Change Log Footer */}
            {activeVersion && (
              <div className="p-3 bg-surface-50 dark:bg-surface-850 border-t border-surface-200 dark:border-surface-800 text-[11px] text-surface-500">
                <span className="font-bold">Change note:</span> {activeVersion.changeLog || 'Initial upload'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
